/* The country registry for checkout v2 — the single source of truth.
 *
 * ADDING A COUNTRY: add one entry below and seed its Firestore docs. Nothing
 * else. The bake script generates its page, the Edge Function starts routing to
 * it, the admin grows a tab for it, and createOrderV2 accepts it — all from this
 * array. No other file needs editing.
 *
 * Lives in functions/ because that is the directory Firebase deploys. The bake
 * script reaches it with require("../functions/countries") — Netlify checks out
 * the whole repo, so one file serves both sides. This deliberately avoids the
 * wart the day list has, where adding a webinar day means editing both
 * scripts/bake-checkout-config.js and functions/create-order.js and the code
 * has to warn you about it.
 *
 * NOTHING HERE IS ADMIN-EDITABLE. Currency and gateway are structural: an admin
 * who could change them could route a country's money to the wrong processor.
 * The admin edits prices, promo codes and content, which live in Firestore.
 */

/* minorFactor is what you multiply a major-unit price by to get the integer
 * amount Stripe and Curlec expect. It is 100 for every currency we take, and
 * the field exists because it is NOT 100 everywhere:
 *
 *   Stripe's zero-decimal list, which use 1:
 *     BIF CLP DJF GNF JPY KMF KRW MGA PYG RWF UGX VND VUV XAF XOF XPF
 *
 * CORRECTION, and the reason this comment names the whole list rather than
 * paraphrasing it: an earlier version of this file claimed IDR was zero-decimal
 * and warned that Indonesia would be charged 100x. That was wrong. IDR is not
 * on the list, and Stripe confirms it directly — a PaymentIntent for 1 IDR is
 * rejected with "Rp0.01 converts to...", i.e. it reads the integer as hundredths.
 * TWD and THB behave the same way. All three are 100.
 *
 * The lesson is the field, not the value: do not infer a currency's exponent
 * from how it is written locally. Ask Stripe with a deliberately tiny amount —
 * the rejection message formats the value back and tells you the exponent.
 *
 * HitPay is the exception that does not use this at all — it takes DECIMAL
 * MAJOR units ("1257.00") where the others take integers. All three conventions
 * go through the shared amount helper; none of them are applied inline.
 */

const COUNTRIES = [
  {
    code: "my",
    label: "Malaysia",
    // The phone-field dial code. Here rather than in the page because it is a
    // country-shaped fact, and the page hardcoding its own list was the one
    // thing that quietly broke the "adding a country is one line" promise — a
    // Thai buyer would have got a form with no +66 option, silently defaulted
    // to +60.
    dial: "+60",
    currency: "MYR",
    minorFactor: 100,
    gateway: "curlec",
    /* CARDS GO TO HITPAY, NOT CURLEC. Decided 2 Nov 2026: Malaysian and
     * Singaporean card payments move to HitPay (domestic MYR cards are
     * 1.2% + RM1 there against Curlec's ~2.4%). FPX and the e-wallets stay on
     * Curlec, which is why `gateway` is still "curlec": it is the DEFAULT for
     * this country's methods and `gateways` overrides it per method. Resolve
     * through gatewayFor() below, never by reading either field directly.
     *
     * HitPay has no embeddable card form, so the card tab is a hosted-checkout
     * redirect: createOrderV2 mints a payment request with
     * payment_methods ["card"] and the page sends the buyer to its `url`;
     * HitPay brings them back to the same checkout page, which then polls
     * orderStatusV2 until the webhook has landed. The raw card fields that
     * used to sit in this page (Razorpay Custom Checkout) are stripped at bake
     * time for any country whose card gateway is not Curlec. */
    gateways: { card: "hitpay" },
    local: "fpx_wallet",
    // Methods the page offers, in tab order.
    methods: ["card", "fpx", "wallet"],
    // Instalments are a Malaysia-only product. The modal and its strings are
    // omitted entirely elsewhere rather than hidden — its copy names Malaysian
    // banks and would be a false claim in any other market.
    instalments: true,
    /* MY USED TO PIN ITSELF TO v1's DOCUMENTS. It no longer does, and the
     * absence of a `docs` override here is the whole of that change:
     *
     *     was:  site_config/vip_checkout       (shared with /checkout/)
     *     now:  site_config/checkout_v2_day1_my
     *
     * The original reasoning was that while both checkouts are live they
     * physically cannot show different Malaysian prices. True — but it also
     * meant they could not show different ones ON PURPOSE. /checkout/ charges
     * `amount` and ignores `compareAmount`, so lowering the v2 sale price
     * silently lowered what the old page charged the moment anyone published.
     * One admin screen quietly repricing a different checkout is a worse
     * failure than the two drifting apart.
     *
     * v1 keeps site_config/vip_checkout untouched, promo codes included, and
     * /checkout/ plus the old /admin/checkout go on reading and writing it.
     * Same business logic on both sides, separate documents underneath.
     *
     * Three things follow from removing this, all of them automatic:
     *   · docFor() falls through to the checkout_v2_dayN_my convention.
     *   · promoDocFor() follows it, so MY's codes now live in the LOCKED
     *     checkout_promos collection like every other market, instead of
     *     staying world-readable on the shared config document.
     *   · publish-checkout-v2.js computes `sharedWithV1` from this very field,
     *     so publishing MY from /admin/checkout-v2 stops mirroring into v1.
     *
     * The documents were created by scripts/split-my-v2-docs.js BEFORE this
     * line was removed. That order is not optional: with no override and no
     * document, the bake refuses to write MY's page — and
     * public/checkout-v2-dayN/index.html, the Edge Function's fail-safe, is a
     * byte copy of that page. */
  },
  {
    code: "sg",
    dial: "+65",
    label: "Singapore",
    currency: "SGD",
    minorFactor: 100,
    /* Was "stripe" until 2 Nov 2026. Singapore's card tab now goes through
     * HitPay's hosted checkout (same decision and same mechanism as Malaysia's
     * card tab, see MY above), so HitPay is the whole of Singapore: cards via
     * redirect, PayNow via the embedded QR. Stripe no longer appears on the SG
     * page at all — no SDK, no publishable key, no Payment Element. */
    gateway: "hitpay",
    // PayNow is the only HitPay rail that crosses currency on our Malaysian
    // account — confirmed against the production API, which returned a raw EMV
    // payload. ShopeePay and GrabPay are MYR-domestic and reject SGD outright.
    local: "hitpay_paynow",
    methods: ["card", "paynow"],
    instalments: false,
  },
  {
    code: "au",
    dial: "+61",
    label: "Australia",
    currency: "AUD",
    minorFactor: 100,
    gateway: "stripe",
    // No local rail. BECS and Afterpay need an Australian Stripe entity, and
    // both are poor fits for a one-off high-ticket sale anyway.
    local: null,
    methods: ["card"],
    instalments: false,
  },
  {
    code: "bn",
    dial: "+673",
    label: "Brunei",
    currency: "BND",
    minorFactor: 100,
    gateway: "stripe",
    // Stripe has no Brunei local payment method at all, so BN buyers are
    // card-and-wallet only — which also means they never leave the page.
    local: null,
    methods: ["card"],
    instalments: false,
  },

  {
    code: "tw",
    dial: "+886",
    label: "Taiwan",
    currency: "TWD",
    minorFactor: 100,
    gateway: "stripe",
    local: null,
    methods: ["card"],
    instalments: false,
    // TWD is two-decimal for CHARGES — it is not on the zero-decimal list — but
    // Stripe treats it as zero-decimal for PAYOUTS: a manual payout amount must
    // be evenly divisible by 100. That is a Dashboard concern, not ours, and it
    // does not affect what we send. Noted because "TWD is zero-decimal" is half
    // true and the half that is true is the half we don't touch.
    // https://docs.stripe.com/currencies#special-cases
  },
  {
    code: "th",
    dial: "+66",
    label: "Thailand",
    currency: "THB",
    minorFactor: 100,
    gateway: "stripe",
    // PromptPay is the obvious local rail and is deliberately not here: Stripe
    // offers it to Thailand and Singapore accounts, and ours is Malaysian.
    local: null,
    methods: ["card"],
    instalments: false,
    // THB's Stripe minimum is 10 BAHT, not the 0.50 that USD/SGD/AUD use — the
    // only currency we take where the flat per-gateway floor is wrong. See
    // minChargeable in create-order-v2.js, which reads this.
    // https://docs.stripe.com/currencies#minimum-charge-amount-by-currency
    minMajor: 10,
  },
  {
    code: "id",
    dial: "+62",
    label: "Indonesia",
    currency: "IDR",
    // 100, NOT 1 — see the CORRECTION at the top of this file. IDR is absent
    // from Stripe's zero-decimal list and the API reads the integer as
    // hundredths, confirmed against our own account.
    minorFactor: 100,
    gateway: "stripe",
    local: null,
    methods: ["card"],
    instalments: false,

    /* AMERICAN EXPRESS CANNOT PAY THE INDONESIAN PRICE, and this is expected.
     *
     * Stripe caps card amounts by DIGIT COUNT: 12 digits for most networks but
     * only 9 for Amex, i.e. a ceiling of 999999999 minor units = IDR
     * 9,999,999.99. Our prices are IDR 26,400,000 / 17,600,000, which are
     * 2640000000 / 1760000000 — ten digits. Visa, Mastercard and JCB are
     * unaffected; Amex will decline, and Amex's share in Indonesia is small
     * enough that repricing under a ten-million-rupiah ceiling would cost far
     * more than it saves.
     *
     * Written down because the decline surfaces as a generic card error at the
     * till with nothing pointing at the amount, and the natural first guess is
     * that IDR is broken rather than that one network is.
     * https://docs.stripe.com/currencies#maximum-charge-amounts */
  },

  // Added 21 Sep 2026.
  {
    code: "hk",
    dial: "+852",
    label: "Hong Kong",
    currency: "HKD",
    minorFactor: 100,
    gateway: "stripe",
    /* No local rail. Hong Kong's instant rail is FPS, which Stripe does not
     * offer, and the account's active capabilities (PLAN §10: card, fpx,
     * grabpay, link, transfers) contain nothing Hong Kong-domestic either. Card
     * carries the market, with Apple Pay and Google Pay arriving through the
     * Payment Element as they do for AU and BN. */
    local: null,
    methods: ["card"],
    instalments: false,
    /* HKD's Stripe minimum is HK$4.00 — eight times the 0.50 that USD, SGD and
     * AUD use, and the SECOND currency we take where the flat per-gateway floor
     * is wrong. Thailand was the first, and this is the evidence that THB was
     * not a one-off worth special-casing: the floor genuinely varies, so
     * minChargeable reads it from here rather than assuming.
     *
     * It never binds at a four-figure ticket. Carried anyway, because the job
     * of the floor is to be right about the gateway, not about today's prices —
     * the failure it prevents is a price that passes validation in the admin
     * and is refused at the till, where nobody is watching.
     * https://docs.stripe.com/currencies#minimum-charge-amount-by-currency */
    minMajor: 4,
  },
];

const DAYS = [1, 2];

const byCode = COUNTRIES.reduce((out, c) => {
  out[c.code] = c;
  return out;
}, {});

/* Returns the country or null. Callers MUST treat null as "reject the request"
 * rather than falling back to a default: `country` arrives from the client, and
 * a miss means someone is sending something we never published. */
function getCountry(code) {
  if (typeof code !== "string") return null;
  return byCode[code.toLowerCase()] || null;
}

function isCountry(code) {
  return getCountry(code) !== null;
}

/* Firestore path for a (day, country) config doc.
 *
 * An allowlist by construction: `code` is resolved through the registry before
 * it is used, so a request value can never be interpolated into a document
 * path. Same rule the existing create-order.js CONFIG_DOCS follows, and for the
 * same reason — interpolating would let a caller point the price lookup at any
 * document in the database. */
function docFor(day, code) {
  const country = getCountry(code);
  if (!country) return null;
  const d = DAYS.indexOf(Number(day)) === -1 ? 1 : Number(day);
  if (country.docs && country.docs[d]) return country.docs[d];
  return `site_config/checkout_v2_day${d}_${country.code}`;
}

/* ---------------- amounts ----------------
 *
 * Three gateways, three conventions, one place to get them wrong:
 *
 *   Curlec / Razorpay  integer minor units   399800
 *   Stripe             integer minor units   125700
 *   HitPay             DECIMAL major units   "1257.00"
 *
 * Everything converts here. Nothing multiplies or divides by 100 inline. */

/* ---------------------------------------------------------------------------
 * Which processor takes a given method's money
 * ------------------------------------------------------------------------- */

/* THE ONE ROUTING RULE. createOrderV2 charges through it, quotePromoV2 prices
 * through it, the bake script decides which markup blocks a page keeps through
 * it, and the page itself is handed the resolved map so it never re-derives
 * any of this. Nothing else may read `gateway`, `gateways` or `local` to decide
 * where money goes.
 *
 * Precedence, most specific first:
 *   1. a per-method override in `gateways` (MY: card -> hitpay)
 *   2. the PayNow convention: `local === "hitpay_paynow"` puts paynow on HitPay
 *   3. the country's default `gateway`
 *
 * Returns the gateway name as a string. It does not check that the method is
 * one the country offers — callers validate `methods` first, and a quote for a
 * method a country lacks is a 400 upstream, not a routing question. */
function gatewayFor(country, method) {
  const c = typeof country === "string" ? getCountry(country) : country;
  if (!c) throw new Error("gatewayFor: unknown country");
  const m = String(method || "card");
  if (c.gateways && c.gateways[m]) return c.gateways[m];
  if (m === "paynow" && c.local === "hitpay_paynow") return "hitpay";
  return c.gateway;
}

/* Every gateway a country's page can reach, deduplicated, in method order.
 * MY -> ["hitpay", "curlec"], SG -> ["hitpay"], AU -> ["stripe"]. This is what
 * decides which SDKs and publishable keys a page is given and which template
 * blocks survive the bake — a page is handed exactly the processors its tabs
 * can reach and nothing else. */
function gatewaysUsed(country) {
  const c = typeof country === "string" ? getCountry(country) : country;
  if (!c) throw new Error("gatewaysUsed: unknown country");
  const out = [];
  (c.methods || ["card"]).forEach((m) => {
    const g = gatewayFor(c, m);
    if (!out.includes(g)) out.push(g);
  });
  return out;
}

/* The per-method map the page is baked with, fully resolved:
 * { card: "hitpay", fpx: "curlec", wallet: "curlec" }. */
function gatewayMap(country) {
  const c = typeof country === "string" ? getCountry(country) : country;
  if (!c) throw new Error("gatewayMap: unknown country");
  const map = {};
  (c.methods || ["card"]).forEach((m) => {
    map[m] = gatewayFor(c, m);
  });
  return map;
}

/* Major units (what an admin types, what a buyer reads) -> integer minor units.
 *
 * IT REFUSES BAD INPUT RATHER THAN RETURNING A NUMBER-SHAPED NON-NUMBER. This
 * used to be `Math.round(Number(major) * minorFactor)` with no guard, and both
 * of its failure modes reached production:
 *
 *   toMinor(undefined)  -> NaN   Firestore stores NaN quite happily, so a
 *   toMinor("1,257.00") -> NaN   captured sale was recorded with a NaN amount
 *                                and silently vanished from every total.
 *   toMinor(null)       -> 0     worse: a real sale recorded as worth nothing,
 *   toMinor("")         -> 0     which passes every integer check downstream.
 *
 * A real HitPay order is in the first state. Throwing is the right answer for
 * all of them: every caller either passes a constant, or has already parsed and
 * validated its input, so the only thing this can break is the case that was
 * already broken. A loud failure at the boundary beats a quiet zero in the
 * database, because nothing downstream can tell a zero-value sale from a
 * zero-value bug. */
function toMinor(major, country) {
  const c = typeof country === "string" ? getCountry(country) : country;
  if (!c) throw new Error("toMinor: unknown country");
  if (major === null || major === undefined || major === "") {
    throw new Error(`toMinor: no amount given (${JSON.stringify(major)})`);
  }
  const n = Number(major);
  if (!Number.isFinite(n)) {
    throw new Error(`toMinor: unparseable amount ${JSON.stringify(major)}`);
  }
  return Math.round(n * c.minorFactor);
}

/* The same conversion, sized by a CURRENCY rather than by a country.
 *
 * Needed because a gateway may report one leg of a transaction in a currency
 * the buyer never saw. HitPay does exactly this: a Singapore PayNow sale is
 * charged in SGD and its fee comes back in MYR, because MYR is what the account
 * settles to. Sizing that fee with Singapore's subunit would be right by luck
 * here — both are 100 — and wrong the moment a zero-decimal currency is
 * involved on either side.
 *
 * Resolved through the registry rather than a second table of currency codes,
 * so there is still one list. Returns null for a currency we do not sell in,
 * and the caller must treat that as "do not guess" — the whole reason this file
 * exists is that a silently wrong minor unit is invisible downstream. */
function factorForCurrency(currency) {
  const want = String(currency || "").toUpperCase();
  if (!want) return null;
  const match = COUNTRIES.find((c) => c.currency === want);
  return match ? match.minorFactor : null;
}

function toMinorInCurrency(major, currency) {
  const factor = factorForCurrency(currency);
  if (factor === null) {
    throw new Error(`toMinorInCurrency: unknown currency ${JSON.stringify(currency)}`);
  }
  if (major === null || major === undefined || major === "") {
    throw new Error(`toMinorInCurrency: no amount given (${JSON.stringify(major)})`);
  }
  const n = Number(major);
  if (!Number.isFinite(n)) {
    throw new Error(`toMinorInCurrency: unparseable amount ${JSON.stringify(major)}`);
  }
  return Math.round(n * factor);
}

/* Integer minor units -> major units, as a Number. For display and for HitPay. */
function toMajor(minor, country) {
  const c = typeof country === "string" ? getCountry(country) : country;
  if (!c) throw new Error("toMajor: unknown country");
  return Number(minor) / c.minorFactor;
}

/* HitPay wants a decimal string. Zero-decimal currencies get no decimal part,
 * which is why this reads minorFactor rather than assuming two places. */
function toHitPayAmount(minor, country) {
  const c = typeof country === "string" ? getCountry(country) : country;
  if (!c) throw new Error("toHitPayAmount: unknown country");
  const places = c.minorFactor === 1 ? 0 : String(c.minorFactor).length - 1;
  return toMajor(minor, c).toFixed(places);
}

/* Buyer-facing string, e.g. "SGD 1,257" or "RM 3,998".
 * Malaysia keeps the RM symbol it uses today; everything else uses the ISO code,
 * which is unambiguous for a cross-border buyer in a way "$" is not — AUD, BND
 * and SGD would otherwise all render as "$". */
function formatAmount(minor, country) {
  const c = typeof country === "string" ? getCountry(country) : country;
  if (!c) throw new Error("formatAmount: unknown country");
  const major = toMajor(minor, c);
  const places = c.minorFactor === 1 ? 0 : 2;
  const body = major.toLocaleString("en-US", {
    minimumFractionDigits: major % 1 === 0 ? 0 : places,
    maximumFractionDigits: places,
  });
  return c.code === "my" ? `RM ${body}` : `${c.currency} ${body}`;
}

module.exports = {
  COUNTRIES,
  DAYS,
  getCountry,
  isCountry,
  docFor,
  gatewayFor,
  gatewaysUsed,
  gatewayMap,
  toMinor,
  toMinorInCurrency,
  factorForCurrency,
  toMajor,
  toHitPayAmount,
  formatAmount,
};
